import type { ReferralAttribution } from '@hero/schema';
import type { Business } from '../config/app-registry.config';

/**
 * PostHog property names for the referral program. One list so the
 * landing page, the dashboard, the mobile apps, `hero-r.js`, the
 * backend stamp, and the HogQL queries in the admin panel all spell
 * them the same way.
 *
 * Two layers, same names:
 *
 * - **Event super-properties** (`posthog.register`) — stamped on every
 *   event a browser / device sends after it sees a code, so an
 *   anonymous funnel can be broken down by `referral_code` before we
 *   know who the person is.
 * - **Person properties** (`$set_once`) — written by the backend when
 *   a lead / account is attributed, and by the client identify hooks
 *   from `User.meta.referral`. `$set_once` keeps the **original**
 *   (first-touch) attribution forever.
 *
 * `referral_code_current` is the exception: it is a plain register /
 * `$set` value that follows the **latest** code the browser saw, so a
 * later campaign link can be analysed without stealing the original's
 * credit.
 */
export const REFERRAL_PH_PROPS = {
  /**
   * The code that owns the person. On the Person (`$set_once`, backend):
   * the link they converted through. On a browser's events
   * (super-property): its first-touch code.
   */
  code: 'referral_code',
  /** Latest code seen on this browser / device. Follows the last touch. */
  codeCurrent: 'referral_code_current',
  /** First-touch code when it differed from the one that converted. Person only. */
  codeOriginal: 'referral_code_original',
  /** `ReferralPartner._id` as a string. */
  partnerId: 'referral_partner_id',
  /** Partner display name, when the writer knows it (backend only). */
  partnerName: 'referral_partner',
  /** `ReferralLink._id` as a string. */
  linkId: 'referral_link_id',
  /** `direct` | `closest_tracking` | `manual`. */
  mode: 'referral_mode',
  /** Which line the capture / attribution belongs to. */
  business: 'referral_business',
  /** The `hero_vid` first-party cookie / device id. */
  visitorId: 'referral_visitor_id',
  /** ISO timestamp of the original capture / attribution. */
  at: 'referral_at',
} as const;

export type ReferralPostHogProps = Record<string, string>;

/**
 * Super-properties a client registers once it holds a code. Pass the
 * stored original code and, when different, the most recent one.
 */
export function getReferralSuperProperties(state: {
  code?: string;
  currentCode?: string;
  visitorId?: string;
  business?: Business;
}): ReferralPostHogProps {
  const props: ReferralPostHogProps = {};
  if (state.code) props[REFERRAL_PH_PROPS.code] = state.code;
  const current = state.currentCode ?? state.code;
  if (current) props[REFERRAL_PH_PROPS.codeCurrent] = current;
  if (state.visitorId) props[REFERRAL_PH_PROPS.visitorId] = state.visitorId;
  if (state.business) props[REFERRAL_PH_PROPS.business] = state.business;
  return props;
}

/**
 * `$set_once` person properties from a persisted attribution
 * (`User.meta.referral`, `Org.meta.referral`, `Lead.meta.sourceMeta.referral`).
 * Safe to send on every identify — PostHog keeps the first write.
 */
export function getReferralPersonPropertiesOnce(
  attribution: ReferralAttribution | undefined,
  extra: { partnerName?: string; business?: Business } = {},
): ReferralPostHogProps {
  if (!attribution) return {};
  const props: ReferralPostHogProps = {
    [REFERRAL_PH_PROPS.code]: attribution.code,
    [REFERRAL_PH_PROPS.partnerId]: attribution.partnerId.toString(),
    [REFERRAL_PH_PROPS.linkId]: attribution.linkId.toString(),
    [REFERRAL_PH_PROPS.mode]: attribution.mode,
    [REFERRAL_PH_PROPS.at]: new Date(attribution.at).toISOString(),
  };
  if (attribution.originalCode) props[REFERRAL_PH_PROPS.codeOriginal] = attribution.originalCode;
  if (attribution.visitorId) props[REFERRAL_PH_PROPS.visitorId] = attribution.visitorId;
  if (extra.partnerName) props[REFERRAL_PH_PROPS.partnerName] = extra.partnerName;
  if (extra.business) props[REFERRAL_PH_PROPS.business] = extra.business;
  return props;
}

/**
 * Referral **metadata on an action event** (`auth_sign_up`, a lead
 * event, …). Events describe what the person did; referral rides along
 * as properties under the same names as the person / super properties,
 * so a HogQL breakdown by `referral_code` works on every layer without
 * a dedicated "referral happened" event.
 */
export type ReferralEventProps = {
  referral_code?: string;
  referral_partner_id?: string;
  referral_link_id?: string;
  referral_mode?: ReferralAttribution['mode'];
  referral_business?: Business;
};

export function getReferralEventProps(
  attribution: ReferralAttribution | undefined,
  extra: { business?: Business } = {},
): ReferralEventProps {
  if (!attribution) return {};
  return {
    [REFERRAL_PH_PROPS.code]: attribution.code,
    [REFERRAL_PH_PROPS.partnerId]: attribution.partnerId.toString(),
    [REFERRAL_PH_PROPS.linkId]: attribution.linkId.toString(),
    [REFERRAL_PH_PROPS.mode]: attribution.mode,
    ...(extra.business ? { [REFERRAL_PH_PROPS.business]: extra.business } : {}),
  };
}

/**
 * Org-group properties mirroring the person ones, so org-level insights
 * ("revenue by referral partner") work without joining on people.
 */
export function getReferralOrgGroupProperties(
  attribution: ReferralAttribution | undefined,
  extra: { partnerName?: string } = {},
): ReferralPostHogProps {
  if (!attribution) return {};
  const props: ReferralPostHogProps = {
    [REFERRAL_PH_PROPS.code]: attribution.code,
    [REFERRAL_PH_PROPS.partnerId]: attribution.partnerId.toString(),
    [REFERRAL_PH_PROPS.linkId]: attribution.linkId.toString(),
    [REFERRAL_PH_PROPS.mode]: attribution.mode,
  };
  if (extra.partnerName) props[REFERRAL_PH_PROPS.partnerName] = extra.partnerName;
  return props;
}
